import { useEffect, useState, useCallback } from 'react';
import {
    Wallet, TrendingUp, ClipboardCheck, AlertTriangle, Plus,
    ArrowRightLeft, ScrollText, BarChart3, Settings, Loader2,
} from 'lucide-react';
import { ACCENT, OK, BAD, WARN, VIOLET, tableSurfaces } from '../../lib/uiTokens';

/** Sekce Pokladny otevírané v bočním inspektoru (dashboard je hlavní stránka). */
const SEKCE = [
    { id: 'pohyby',     label: 'Pohyby',     Icon: ArrowRightLeft },
    { id: 'vyuctovani', label: 'Vyúčtování', Icon: ScrollText },
    { id: 'audity',     label: 'Audity',     Icon: ClipboardCheck },
    { id: 'pohledavky', label: 'Pohledávky', Icon: AlertTriangle },
    { id: 'reporty',    label: 'Reporty',    Icon: BarChart3 },
    { id: 'nastaveni',  label: 'Nastavení',  Icon: Settings },
];

const fmtKc = (v) => new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v ?? 0)) + ' Kč';

export default function PokladnaPrehledTab({
    cardStyle, isDark, refreshKey,
    onOpenFlek, onProvestAudit, onPridatPohyb, onOtevritNovyFlek, onAuditovatDen,
    // Dashboard režim (Finance → tab Pokladna): otevře sekci v bočním inspektoru
    onOtevritSekci = null, aktivniSekce = null,
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    // silent=true → nepřepneme do loading stavu (UI neblikne při polling refreshi)
    const load = useCallback((silent = false) => {
        if (!silent) setLoading(true);
        fetch('/api/finance/pokladna/prehled', { credentials: 'same-origin' })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(setData).catch(silent ? () => {} : setErr).finally(() => { if (!silent) setLoading(false); });
    }, []);
    useEffect(() => { load(); }, [load, refreshKey]);

    // Auto-refresh každých 5 s — chceme vidět, jak na fleky padají pohyby
    // v reálném čase (typicky když rozvozce zaplatí objednávku Hotově).
    // Pause když je tab schovaný (visibilitychange) — zbytečné pollování v
    // pozadí spotřebovává backend i baterku.
    useEffect(() => {
        const POLL_MS = 5000;
        let iv = setInterval(() => load(true), POLL_MS);
        const onVis = () => {
            clearInterval(iv);
            if (!document.hidden) {
                load(true);
                iv = setInterval(() => load(true), POLL_MS);
            }
        };
        document.addEventListener('visibilitychange', onVis);
        return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
    }, [load]);

    if (loading && !data) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin" style={{ color: ACCENT }} /></div>;
    if (err) return <div className="rounded-2xl p-4 text-sm" style={{ ...cardStyle, color: BAD }}>Chyba načtení: {String(err)}</div>;
    if (!data) return null;

    const hlavniFlek = data.fleky?.find(f => f.typ === 'bistro_hlavni');
    const flekyRozvoz = data.fleky?.filter(f => f.typ === 'rozvoz') ?? [];
    const surf = tableSurfaces(isDark);

    return (
        <div className="space-y-5">
            {/* Stat strip — karty s vazbou na sekci se dají prokliknout do inspektoru */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <StatCard cardStyle={cardStyle} icon={<Wallet size={16} />} label="Hotovost celkem" value={fmtKc(data.hotovost_celkem_kc)} accent={ACCENT} />
                <StatCard cardStyle={cardStyle} icon={<TrendingUp size={16} />} label="Týden saldo" value={fmtKc(data.tyden_saldo_kc)} accent={data.tyden_saldo_kc >= 0 ? OK : BAD}
                    onClick={onOtevritSekci && (() => onOtevritSekci('reporty'))} />
                <StatCard cardStyle={cardStyle} icon={<ClipboardCheck size={16} />} label="Fronta ke schválení" value={String(data.fronta_ke_schvaleni ?? 0)} accent={WARN}
                    onClick={onOtevritSekci && (() => onOtevritSekci('vyuctovani'))} aktivni={aktivniSekce === 'vyuctovani'} />
                <StatCard cardStyle={cardStyle} icon={<AlertTriangle size={16} />} label="Otevřené pohledávky" value={String(data.pohledavky_otevrene ?? 0)} accent={BAD}
                    onClick={onOtevritSekci && (() => onOtevritSekci('pohledavky'))} aktivni={aktivniSekce === 'pohledavky'} />
            </div>

            {/* Vstupy do sekcí — vše se otevře v bočním inspektoru */}
            {onOtevritSekci && (
                <div className="flex flex-wrap gap-2">
                    {SEKCE.map(s => {
                        const Icon = s.Icon;
                        const aktivni = aktivniSekce === s.id;
                        return (
                            <button
                                key={s.id}
                                onClick={() => onOtevritSekci(s.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                style={{
                                    ...cardStyle,
                                    color: aktivni ? ACCENT : 'var(--text-secondary)',
                                    borderColor: aktivni ? ACCENT : undefined,
                                }}
                            >
                                <Icon size={13} />
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
                <button onClick={onOtevritNovyFlek}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white"
                    style={{ background: ACCENT }}>
                    <Plus size={14} /> Otevřít flek
                </button>
                {hlavniFlek?.aktivni_smena && (
                    <button onClick={() => onPridatPohyb({ id: hlavniFlek.aktivni_smena.id, pokladna: { nazev: hlavniFlek.nazev, typ: 'bistro_hlavni' } })}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
                        style={{ ...cardStyle, color: 'var(--text-primary)' }}>
                        <Plus size={14} /> Přidat pohyb
                    </button>
                )}
                {hlavniFlek?.aktivni_smena && (
                    <button onClick={() => onAuditovatDen({ id: hlavniFlek.aktivni_smena.id })}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
                        style={{ background: `${ACCENT}1f`, color: ACCENT, border: `1px solid ${ACCENT}44` }}>
                        <ClipboardCheck size={14} /> Audit hlavního fleku
                    </button>
                )}
            </div>

            {/* 2-column: pohyby dnes + fleky */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Pohyby dnes */}
                <div className="lg:col-span-2 rounded-2xl p-5" style={cardStyle}>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Pohyby dnes</h3>
                        {onOtevritSekci && (
                            <button
                                onClick={() => onOtevritSekci('pohyby')}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold"
                                style={{ background: `${ACCENT}14`, color: ACCENT, border: `1px solid ${ACCENT}44` }}
                                title="Zobrazit všechny pohyby"
                            >
                                <ArrowRightLeft size={12} />
                                Všechny
                            </button>
                        )}
                    </div>
                    {(!data.pohyby_dnes || data.pohyby_dnes.length === 0) ? (
                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Žádné pohyby dnes.</p>
                    ) : (
                        <div className="overflow-auto" style={{ maxHeight: 400 }}>
                            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: surf.headBg }}>
                                        <PTh align="left">Čas</PTh><PTh align="left">Flek</PTh><PTh align="left">Typ</PTh><PTh>Kč</PTh><PTh align="left">Vazba</PTh>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.pohyby_dnes.map(p => (
                                        <tr key={p.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                            <td className="px-3 py-2 text-[12.5px] tabular-nums" style={{ color: 'var(--text-secondary)' }}>{new Date(p.created_at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}</td>
                                            <td className="px-3 py-2 text-[12.5px]" style={{ color: 'var(--text-primary)' }}>{p.smena?.pokladna?.nazev}</td>
                                            <td className="px-3 py-2"><TypBadge typ={p.typ} /></td>
                                            <td className="px-3 py-2 text-right text-[12.5px] font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmtKc(p.castka_kc)}</td>
                                            <td className="px-3 py-2 text-[12.5px]" style={{ color: 'var(--text-tertiary)' }}>{p.objednavka?.cislo_objednavky ?? p.doklad?.popis?.slice(0, 30) ?? p.poznamka?.slice(0, 30) ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Fleky */}
                <div className="space-y-3">
                    <FlekCard cardStyle={cardStyle} flek={hlavniFlek} onOpen={onOpenFlek} onProvestAudit={onProvestAudit} accent="#f59e0b" />
                    {flekyRozvoz.map(f => (
                        <FlekCard key={f.id} cardStyle={cardStyle} flek={f} onOpen={onOpenFlek} onProvestAudit={onProvestAudit} accent={ACCENT} />
                    ))}
                </div>
            </div>
        </div>
    );
}

// Hlavička tabulky pohybů — sjednoceno s PnlTabulkou Výsledovky.
function PTh({ children, align = 'right' }) {
    return (
        <th className={`px-3 py-2.5 text-[10.5px] font-bold uppercase tracking-wide ${align === 'left' ? 'text-left' : 'text-right'}`}
            style={{ color: 'var(--text-tertiary)' }}>{children}</th>
    );
}

// KPI dlaždice ve stylu Výsledovky: tintovaná ikonka + label uppercase + velká hodnota.
function StatCard({ icon, label, value, accent, cardStyle, onClick = null, aktivni = false }) {
    return (
        <div
            onClick={onClick || undefined}
            role={onClick ? 'button' : undefined}
            className={'rounded-2xl p-4' + (onClick ? ' cursor-pointer transition-colors hover:opacity-90' : '')}
            style={{ ...cardStyle, borderColor: aktivni ? accent : undefined }}
        >
            <div className="flex items-center gap-2 mb-1.5">
                <span className="rounded-md p-1 flex-shrink-0" style={{ background: `${accent}1f`, color: accent }}>{icon}</span>
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>{label}</span>
            </div>
            <div className="text-2xl font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{value}</div>
        </div>
    );
}

function FlekCard({ flek, cardStyle, onOpen, onProvestAudit, accent }) {
    if (!flek) return null;
    const otev = flek.aktivni_smena;
    return (
        <div className="rounded-2xl p-4" style={{ ...cardStyle, borderLeft: `3px solid ${accent}` }}>
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{flek.typ === 'bistro_hlavni' ? 'Hlavní pokladna' : 'Rozvoz'}</div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{flek.nazev}</div>
                </div>
                <button onClick={() => onProvestAudit({ id: flek.id, nazev: flek.nazev, ocekavany_kc: otev?.aktualni_kc ?? null })}
                    className="text-xs px-2 py-1 rounded-lg"
                    style={{ background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>Audit</button>
            </div>
            {otev ? (
                <div className="mt-2 cursor-pointer" onClick={() => onOpen(otev.id)}>
                    <div className="text-base font-bold" style={{ color: accent }}>{fmtKc(otev.aktualni_kc)}</div>
                    <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        {otev.zamestnanec ?? '—'} · od {new Date(otev.otevreno_at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
            ) : (
                <div className="mt-2 text-xs italic" style={{ color: 'var(--text-tertiary)' }}>Zavřený</div>
            )}
        </div>
    );
}

function TypBadge({ typ }) {
    const map = {
        prijem_hotovost: { c: '#10b981', l: 'Příjem' },
        prevod_z_hlavniho: { c: '#3b82f6', l: 'Z hlavního' },
        odvod_do_hlavniho: { c: VIOLET, l: 'Odvod' },
        vklad_majitel: { c: '#10b981', l: 'Vklad CEO' },
        vyber_majitel: { c: '#ef4444', l: 'Výběr CEO' },
        prevod_z_banky: { c: '#10b981', l: 'Z banky' },
        prijem_hotovost_doplatek: { c: '#10b981', l: 'Doplatek' },
        vraceni_hotovost: { c: '#ef4444', l: 'Vráceno hostovi' },
        odvod_do_banky: { c: '#3b82f6', l: 'Do banky' },
        zaloha_mzdy: { c: '#f59e0b', l: 'Záloha mzdy' },
        hotovostni_vydaj: { c: '#ef4444', l: 'Výdaj' },
        korekce_manko: { c: '#ef4444', l: 'Manko' },
        korekce_prebytek: { c: '#10b981', l: 'Přebytek' },
        pocatecni_vklad: { c: '#6b7280', l: 'Vklad' },
        prijem_dysko_hotovost: { c: '#06b6d4', l: 'Dýško hot.' },
        prijem_dysko_karta: { c: '#06b6d4', l: 'Dýško karta' },
        prijem_dysko_online: { c: '#06b6d4', l: 'Dýško online' },
        osobni_odber_zamestnanec: { c: '#84cc16', l: 'Osobní odběr' },
        vyplata_z_hlavniho: { c: '#ef4444', l: 'Výplata' },
        storno_vyplaty: { c: '#10b981', l: 'Storno výplaty' },
    };
    const { c, l } = map[typ] ?? { c: '#6b7280', l: typ };
    return <span style={{ background: c + '22', color: c, padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 500 }}>{l}</span>;
}

export { fmtKc, TypBadge };
